/* ── BRIEF 05 STEP 6 (via brief 124 §5) — THE CHOOSER ───────────────────────
 *
 * Three buttons on the descriptor. ONE PLAIN COMPONENT IN `src/components/`,
 * and deliberately NOT a seventh modal shell grown inside `App.tsx` (§B.3).
 *
 * ── BRIEF 129 §2 — AND NOW TWO SHAPES, IN ONE COMPONENT ────────────────────
 *
 * The 127 verdict left one question open and this § answers both halves of it.
 *
 *   THE STATE: one, a discriminated union (`dropSubject`, App.tsx). Its whole
 *   argument is over there, beside the state itself.
 *
 *   THE COMPONENT: one, with two shapes — NOT two components. What differs
 *   between an email drop and a document drop is a summary block and a list of
 *   options. What is IDENTICAL is the modal frame, the title's grammar, the
 *   tinted-option primitive, the palette wiring, and the rule that the chooser
 *   decides nothing and reports a choice. Forking would duplicate five things
 *   to vary two, and this tree has ruled that shape twice inside a month —
 *   "one presentation, two mounts" (128 §4), "one row renderer, two surfaces"
 *   (125 §6). The second shape is a branch, not a second file.
 *
 *   THE FILE KEEPS ITS NAME, deliberately. `EmailDropChooser.tsx` is cited by
 *   three briefs, by `App.tsx:7597` and by three rows of
 *   `scripts/lint/i18n-exits-baseline.json`. A rename would edit a lint
 *   baseline and break citations in the record for a cosmetic gain, and
 *   renaming is not what this § was given. Said here so the name is a known
 *   debt rather than a claim.
 *
 * It decides nothing about the prefills — it reports which act was chosen and
 * the caller opens the app's own modal. That is what keeps the prefill keys in
 * one place beside `openModal`, where the arms they have to match live.
 *
 * ── THE TIME-ENTRY OPTION CAN BE DISABLED, AND IT SAYS WHY ─────────────────
 *
 * An OWA list-row drop yields a subject and an EWS item id, and no translator
 * for that id exists in this tree. So `autoMinutes` is null and the option is
 * DISABLED with a tooltip naming the reason. Do not fake a number: a minute
 * count invented here would be indistinguishable, on the screen and in the
 * invoice, from one the billing library computed.
 *
 * ── ALREADY BILLED IS A WARNING, NOT A REFUSAL ─────────────────────────────
 *
 * The hard guarantee is the database — `@@unique([caseId, emailGraphId,
 * collaboratorId])` and its `ccId` twin, with `timeEntries.js` turning a P2002
 * into a friendly 409. This warns BEFORE the modal opens so the user is not
 * told after typing.
 */
import { useState } from "react";
import { useTranslation } from "../i18n";
import { ModalLayout } from "./SharedUI";
import { FS, RAD } from "../lib/tokens";
import { matterEventTitle } from "../lib/matterLabel";
import { withCount } from "../lib/pluralVars"; // 216 §5 — the title counts the files (ruling 16)
import type { MatterKind } from "./MatterPicker"; // 131 §6
import type { EmailDropDescriptor } from "./EmailDropLayer";

export type EmailDropChoice = "task" | "event" | "time";
/** 129 §2 — Laurent's four, in his order. `file` is "just file it" (§3), `task`
 *  and `event` are §4, `bill` is §5. */
export type DocumentDropChoice = "file" | "task" | "event" | "bill";

/** 130 §1 — a matter, as the second step needs it: an id and the two halves of
 *  its label. `matterEventTitle` composes them, so the row this list shows and
 *  the title a new event gets are the same string built the same way. */
export type DropMatter = {
  id: number;
  ref?: string | null;
  title?: string | null;
  /* 131 §6 — WHICH LIST IT CAME OUT OF, and it is load-bearing three times:
     the upload route forks on it (`cases/:id/files/upload` vs
     `collective-cases/:id/files/upload`), the permission is asked as `{ccId}`
     rather than `{caseId}`, and — the quiet one — a Case id and a
     CollectiveCase id are drawn from DIFFERENT sequences, so `key={m.id}`
     would collide the moment both lists are offered. */
  kind: MatterKind;
};

/** 129 §2 — what the chooser is holding, and it is the caller's own state
 *  shape: one union, so the two arms cannot both be open.
 *
 *  130 §1 — the document arm's `choice` is what makes the second step possible
 *  without a second state: null is "choose an act", set is "choose a matter". */
export type DropSubjectProp =
  /* 184 B.4 — `emlFile` is the dropped `.eml` itself, held in state exactly as
     the document arm holds its File (130 §1): never on the wire until a task
     or event exists to file it under. Absent for a `message:` URI drop and
     for the subject-only drop. */
  | { kind: "email"; email: EmailDropDescriptor; emlFile?: File | null }
  /* 216 §5 [RULED 74] — EVERY document of the drop, through one chooser: one choice, one matter, one folder. */
  | { kind: "document"; files: File[]; choice?: DocumentDropChoice | null };

type Props = {
  subject: DropSubjectProp;
  /* BRIEF 125 §5 — `caseView.emails.billing` for THIS matter, computed by the
     caller (App.tsx `_dropCanBill`) from the same call
     `CaseEmailsSection.tsx:152` makes. It decides whether the third choice is
     OFFERED AT ALL — not whether it is disabled. Optional and defaulting to
     true so the component keeps rendering three options for any caller that
     does not answer the question; the one caller that exists answers it. */
  canBillEmails?: boolean;
  /* 129 §2/§5 — the SAME question for the document shape, and a DIFFERENT key,
     because the two are different permissions: `caseView.documents.billing` is
     what `documentBilling.js:473`'s `/bill` route enforces. Read off the route
     rather than chosen, exactly as 125 §5 read `caseView.emails.billing` off
     the twins. Same doctrine: a user without it sees THREE choices, not a
     disabled fourth. */
  canBillDocuments?: boolean;
  onChoose: (choice: EmailDropChoice) => void;
  /* Two callbacks rather than one, because the two option sets OVERLAP by name
     ("task", "event") and mean different acts. One callback taking a union
     would be a string whose meaning depends on state the callee cannot see. */
  onChooseDocument: (choice: DocumentDropChoice) => void;
  /* ── BRIEF 130 §1 — THE MATTER, WHEN THE SCREEN HAS NONE ──────────────────
     The caller decides WHETHER this step happens: it hands the act back to the
     chooser with `subject.choice` set, and only then is the list read. So this
     component still decides nothing — it reports a choice, then reports a
     matter, and the caller does the filing.

     `matters` is the population the caller measured (131 §6: BOTH kinds,
     archived filtered, each row stamped with the list it came from) and
     is deliberately not filtered again in here. A component that re-decides
     which matters a user may file into is a second permission model. */
  matters?: DropMatter[];
  onChooseMatter?: (id: number, kind: MatterKind) => void;
  /* A mis-click on one of four buttons must not cost the drag. The file lives
     in the caller's state and survives going back; closing the dialog is what
     discards it. */
  onBack?: () => void;
  onClose: () => void;
};

/* ── BRIEF 125 §1 — THE COLOUR CARRIES THE MEANING ─────────────────────────
 *
 * Laurent: the three buttons were "not consistent with app styling"; make them
 * translucent green (task), blue (event), amber (time entry), "as in the
 * timelog modal". The row he means is `TimeEntryModal.tsx:233-344` — four
 * save-mode buttons whose tint says which save they are.
 *
 * IT WAS READ, NOT EYEBALLED, AND WHAT IT SAYS IS A TOKEN DEBT. Those four
 * buttons carry their tint as RAW rgba literals beside a token foreground:
 *
 *   :239-241  rgba(52,211,153,.15) / .3   over  color: var(--gn)   #34D399
 *   :265-267  rgba(74,124,255,.15) / .3   over  color: var(--ac)   #4A7CFF
 *   :296-298  rgba(251,191,36,.15) / .3   over  color: var(--am)   #FBBF24
 *   :325-327  rgba(248,113,113,.15) / .3  over  color: var(--rd)   #F87171
 *
 * Each literal is the token's own base value at 15% / 30% — so the tint is
 * REPRODUCED FROM THE TOKEN rather than copied as a fourth literal (§4.9
 * forbids a hardcoded colour in new UI, and the brief forbids answering a
 * token debt by adding to it). `color-mix(…, transparent)` is this tree's
 * house pattern for a wash from a token (App.css:1157, ScheduleGrid.tsx:325).
 * The rendered colour is identical while the theme holds its base, and it
 * TRACKS the theme where the literals cannot — which is the debt itself, and
 * it is left where it lies: repointing four live buttons is not this §.
 */
const TONE: Record<EmailDropChoice, string> = {
  task: "--gn",
  event: "--ac",
  time: "--am",
};

/* ── BRIEF 129 §2 — THE FOURTH COLOUR ──────────────────────────────────────
 *
 * THREE OF THE FOUR ARE FIXED BY MEANING, and that is 125 §1's ruling applied
 * across two dialogs rather than inside one:
 *
 *   task   --gn   green in BOTH shapes
 *   event  --ac   blue  in BOTH shapes
 *   bill   --am   amber in both — the email chooser's "Time entry" is the same
 *                 act on the other kind
 *
 * Assigning A-B-C-D in list order would have made green mean "task" in one
 * chooser and "just file it" in the other, one dialog apart. That is precisely
 * the thing a meaningful palette may not do.
 *
 * ── AND THE FOURTH IS `--pp`, RULED BY LAURENT, OVERRULING `--rd` ──────────
 *
 * §2 said "a fourth choice takes a fourth colour from that same set, never a
 * new literal", and the first build read "that same set" as the timelog row's
 * four — which put `--rd` on "just file it" because it was the member left
 * over. **The argument for it was true and beside the point.** It IS "Save &
 * Clear" in the row it comes from (`TimeEntryModal.tsx:341`), the terminal
 * variant that carries nothing forward; but a lawyer meeting this dialog does
 * not read the timelog row's vocabulary, they read the APP's — and in the app
 * `--rd` is the danger colour: `unbillable`, the delete confirmations, every
 * refusal toast. Inking the SAFEST of the four in it is a warning where there
 * is no risk.
 *
 * `--pp` is the right member of the right set. It is an EDITABLE PARENT token
 * — "Secondary accent" (`themeDerivation.ts:71`, `#a78bfa`) — with its own
 * derived wash `--ps` ("Violet — soft bg", :87), so it is a first-class
 * citizen of the theme rather than a literal smuggled in: §4.9 is satisfied
 * for exactly the reason the other three are. Neither chooser used it, and it
 * carries no meaning here that collides with one it has elsewhere.
 *
 * WHY THE WASH IS STILL `color-mix(… 15%)` AND NOT `--ps`. `--ps` is 12% and
 * the other three buttons are mixed at 15%; using the ready-made token for one
 * of four would make that button quietly lighter than its neighbours. All four
 * are computed the same way, from their own parent, which is what keeps the row
 * looking like a row.
 */
const DOC_TONE: Record<DocumentDropChoice, string> = {
  file: "--pp",
  task: "--gn",
  event: "--ac",
  bill: "--am",
};

/* MODULE SCOPE, not a body-local const. A PascalCase function declared inside a
   component and mounted as JSX is a NEW COMPONENT TYPE on every render, so React
   unmounts and remounts it — Bug A, which this tree now enforces rather than
   remembers (`scripts/lint-inline-components.js`). Hookless, so its closure
   becomes props. */
function DropOption<C extends string>({
  choice, tone, kind = "email", label, disabled, title, onChoose,
}: {
  choice: C;
  /* 129 §2 — the token comes IN rather than being looked up in here, because
     there are two maps now and the map is the shape's business, not the
     button's. Same primitive, two callers, and GENERIC in the choice so each
     caller keeps its own union all the way to the handler. */
  tone: string;
  kind?: "email" | "document";
  label: string;
  disabled?: boolean;
  title?: string;
  onChoose: (c: C) => void;
}) {
  const v = tone;
  return (
    <button
      type="button"
      className="btn"
      data-drop-choice={kind === "email" ? choice : undefined}
      data-doc-drop-choice={kind === "document" ? choice : undefined}
      disabled={!!disabled}
      title={title}
      onClick={() => onChoose(choice)}
      style={{
        flex: 1,
        padding: "12px 10px",
        fontSize: FS.s12,
        fontWeight: 600,
        borderRadius: RAD.r6,
        /* `.btn` centres nothing — it is `display: inline-flex` with no
           `justify-content`, so three equal-width buttons wore left-hung
           labels. `.modal-save`, the class the timelog row uses, centres
           (App.css:1085). Same treatment, not a new one. */
        justifyContent: "center",
        background: `color-mix(in srgb, var(${v}) 15%, transparent)`,
        color: `var(${v})`,
        border: `1px solid color-mix(in srgb, var(${v}) 30%, transparent)`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {label}
    </button>
  );
}

/* Sizes as a human writes them, and the same arithmetic `DocumentBillingModal`
   uses (`fmtKB`, :44) rather than a second one. */
function fmtSize(bytes: number): string {
  const kb = (Number(bytes) || 0) / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function EmailDropChooser({
  subject, canBillEmails = true, canBillDocuments = true, onChoose, onChooseDocument,
  matters = [], onChooseMatter, onBack, onClose,
}: Props) {
  const { t } = useTranslation();
  /* 130 §1 — BEFORE THE BRANCH, because the document arm returns early and a
     hook after it would run on one shape and not the other (React error #310).
     One piece of local state, and it is a text box's contents: the chooser
     still holds no decision. */
  const [matterQ, setMatterQ] = useState("");

  /* ── 129 §2 — THE DOCUMENT SHAPE ────────────────────────────────────────
     Laurent's four, in his order, on the palette above. It is a 2×2 grid and
     that is not a layout preference: the row this palette comes from lays ITS
     four out as `gridTemplateColumns: "1fr 1fr"` (TimeEntryModal.tsx:225), and
     four long labels in one line would each be four words on two lines.

     THE SUMMARY NAMES THE FILE, which is §1's other half. The classifier's
     residue rule ("anything that is not positively an email is a document") is
     only honest if the app SAYS which it decided — so the title says
     "document", the name is in bold where the email's subject is, and the size
     is beside it. A user who disagrees closes the dialog; nothing is filed
     until a choice is made. */
  if (subject.kind === "document") {
    const files = subject.files;
    /* THE FILE SUMMARY IS THE SAME BLOCK ON BOTH STEPS — the reader is being
       asked a second question about the same file, and re-stating which file
       is what makes the second question answerable. 216 §5 — every file of
       the drop, each with its size. */
    const summary = (
      <div data-doc-drop-files={files.length} style={{ fontSize: FS.s11, color: "var(--ts)", marginBottom: 14, lineHeight: 1.5 }}>
        {files.map((f) => (
          <div key={`${f.name}\u0000${String(f.size)}`} style={{ fontWeight: 600, color: "var(--tp)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {`${f.name} · ${fmtSize(f.size)}`}
          </div>
        ))}
      </div>
    );
    /* 216 §5 [ADVISOR] — the title counts them through the count machinery (ruling 16): `_one` says what
       docDrop.title said (that key loses its reader; its rows leave with the one-shot), `_other` counts. */
    const title = String(t("docDrop.title_count", withCount(files.length, "count", { fallback: files.length === 1 ? "What should this document become?" : `What should these ${String(files.length)} documents become?` })));
    /* ── BRIEF 130 §1 — STEP 2: THE MATTER ──────────────────────────────────
       Reached only when the caller hands the act back with no matter behind
       it. "The SMALLEST possible one: the matter, and nothing else" — so there
       is a search box, a list, and nothing else. No folder, no title, no
       dates: everything after this is the flow that already exists.

       The match is `title + ref`, lower-cased, which is the same test
       `AgendaEventModal.tsx:647` and `TimeEntryModal.tsx:523` apply to the same
       two fields. Every match is rendered — the list scrolls rather than
       truncating, because a matter silently missing from a chooser is a matter
       the user concludes does not exist. */
    if (subject.choice) {
      const q = matterQ.trim().toLowerCase();
      const list = q
        ? matters.filter((m) =>
            ((m.title || "") + " " + (m.ref || "")).toLowerCase().includes(q),
          )
        : matters;
      return (
        <ModalLayout
          title={t("docDrop.matter_title", { fallback: "In which matter should this document be filed?" })}
          onClose={onClose}
          width={520}
        >
          {summary}
          <input
            className="field-input"
            autoFocus
            style={{ padding: "6px 9px", fontSize: FS.s11, marginBottom: 8 }}
            placeholder={t("docDrop.matter_search_ph", { fallback: "Search a matter by title or reference…" })}
            value={matterQ}
            onChange={(e) => setMatterQ(e.target.value)}
          />
          <div
            data-drop-matter-list="1"
            style={{
              maxHeight: 260,
              overflowY: "auto",
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
            }}
          >
            {list.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: FS.s11, color: "var(--tm)" }}>
                {t("docDrop.matter_none", { fallback: "No matter matches that." })}
              </div>
            ) : (
              list.map((m) => (
                <button
                  key={m.kind + "-" + m.id}
                  type="button"
                  data-drop-matter={m.id}
                  data-drop-matter-kind={m.kind}
                  onClick={() => onChooseMatter?.(m.id, m.kind)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "7px 12px",
                    fontSize: FS.s11,
                    background: "none",
                    border: "none",
                    borderBottom: "1px solid var(--br)",
                    color: "var(--tp)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bh)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                >
                  {matterEventTitle(m)}
                </button>
              ))
            )}
          </div>
          {onBack && (
            <button
              type="button"
              className="btn"
              data-drop-back="1"
              onClick={onBack}
              style={{ marginTop: 12, fontSize: FS.s11 }}
            >
              {"← " + t("common.back", { fallback: "Back" })}
            </button>
          )}
        </ModalLayout>
      );
    }
    const opt = (choice: DocumentDropChoice, label: string) => (
      <DropOption
        kind="document"
        choice={choice}
        tone={DOC_TONE[choice]}
        label={label}
        onChoose={onChooseDocument}
      />
    );
    return (
      <ModalLayout
        title={title}
        onClose={onClose}
        /* 166 §C — Laurent: "way too wide" for four short buttons in a
           two-column grid. 520 → 420. This one is a ModalLayout `width` and
           has always worked; it is here because it is the same complaint. */
        width={420}
      >
        {summary}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {opt("file", t("docDrop.just_file", { fallback: "Just file it" }))}
          {opt("task", t("docDrop.and_task", { fallback: "File it & add to a task" }))}
          {opt("event", t("docDrop.and_event", { fallback: "File it & add to an event" }))}
          {/* 125 §5's doctrine, on the other kind and the other key: a person
              who may not bill documents sees THREE choices, not a disabled
              fourth. There is no second question here — a document always has
              a word count to compute from, so nothing corresponds to the email
              shape's "we could not compute a number". */}
          {canBillDocuments && opt("bill", t("docDrop.and_bill", { fallback: "File it & bill it" }))}
        </div>
      </ModalLayout>
    );
  }

  const descriptor = subject.email;
  /* Null minutes is the SEAM the subject-only branch leaves (step 5). It is the
     one field that decides whether this option can be honest. */
  const canTime = descriptor.autoMinutes != null;
  /* BRIEF 125 §5 — TWO QUESTIONS, TWO ANSWERS, AND THEY ARE NOT THE SAME SHAPE.
     "You may not bill email" is about the PERSON: the option is not offered,
     because a control that exists to be refused is noise (§5: "a user without
     it sees two choices, not a disabled third"). "We could not compute a
     number" is about THIS EMAIL: the option is offered and DISABLED with a
     tooltip saying why, because the same user on the next email will be able
     to take it. Collapsing them into one disabled state would tell a user who
     lacks the permission to go and find a better email. */
  const offerTime = canBillEmails;
  const who = descriptor.fromName || descriptor.fromAddress || null;

  return (
    <ModalLayout
      title={t("emailDrop.title", { fallback: "What should this email become?" })}
      onClose={onClose}
      width={520}
    >
      <div style={{ fontSize: FS.s11, color: "var(--ts)", marginBottom: 14, lineHeight: 1.5 }}>
        {descriptor.subject && <div style={{ fontWeight: 600, color: "var(--tp)" }}>{descriptor.subject}</div>}
        {who && <div>{t("emailDrop.sent_by", { fallback: "Sent by" })}: {who}</div>}
        {descriptor.wordCount != null && (
          <div>{t("emailDrop.words", { fallback: "Words" })}: {descriptor.wordCount}</div>
        )}
      </div>

      {descriptor.alreadyBilled && (
        <div
          data-drop-already-billed="1"
          style={{
            fontSize: FS.s11,
            color: "var(--am)",
            border: "1px solid var(--am)",
            borderRadius: RAD.r6,
            padding: "8px 10px",
            marginBottom: 12,
            lineHeight: 1.45,
          }}
        >
          {t("emailDrop.already_billed")}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <DropOption onChoose={onChoose} choice="task" tone={TONE.task} label={t("emailDrop.as_task", { fallback: "Task" })} />
        <DropOption onChoose={onChoose} choice="event" tone={TONE.event} label={t("emailDrop.as_event", { fallback: "Event" })} />
        {offerTime && (
          <DropOption
            onChoose={onChoose}
            choice="time"
            tone={TONE.time}
            label={t("emailDrop.as_time", { fallback: "Time entry" })}
            disabled={!canTime}
            title={canTime ? undefined : t("emailDrop.time_unavailable")}
          />
        )}
      </div>
    </ModalLayout>
  );
}
